import { createSelector } from "@reduxjs/toolkit"

import { spawnArrivals } from "src/utils/spawnTour"

export const selectSelectedCity = (state) => state.ui.selectedCity
export const selectPickingOrigin = (state) => Boolean(state.ui.pickingOrigin)
export const selectSelectedNodes = (state) => state.ui.selectedNodes
export const selectHoveredCity = (state) => state.ui.hoveredCity
export const selectCityModalOpen = (state) => state.ui.cityModalOpen
export const selectAttackModalOpen = (state) => state.ui.attackModalOpen
export const selectMovementTroops = (state) => state.ui.movementTroops

export const selectStrikeOrigin = (state) => state.ui.strikeOrigin
export const selectStrikeTarget = (state) => state.ui.strikeTarget

export const selectMovementIntent = (state) => state.ui.movementIntent

// Stable empty list so a game with no saved order does not hand every subscriber a new [].
const NO_ORDER = []
export const selectCityOrderForGame = (state, gameId) =>
  (gameId != null && state.ui.cityOrder[String(gameId)]) || NO_ORDER

const NO_FACINGS = {}
export const selectStackFacingForGame = (state, gameId) =>
  (gameId != null && state.ui.stackFacing?.[String(gameId)]) || NO_FACINGS

/* Sort city keys by the player's dragged order: listed cities first, in their order; the rest
   keep the order the caller gave them (new cities land at the end, not at random). */
export const orderedByCityOrder = (keys, order) => {
  if (!order.length) return keys
  const rank = new Map(order.map((key, index) => [key, index]))
  return [...keys].sort((a, b) => (rank.get(a) ?? order.length) - (rank.get(b) ?? order.length))
}

/* The survival spawn tour (SV12).

   `selectSpawnArrivals` is the queue: the newcomers in the payload the store holds right now that
   have not been toured yet. It reads the game slice as well as this one, which is the one place
   in this file that does — the alternative is a copy of the game's events in `ui`, and a second
   copy of a payload is how two panels come to disagree about what round it is.

   `selectSpawnTourBusy` is what the round digest waits on, and it is deliberately *derived* from
   the queue rather than only from the `running` flag. The flag is raised by the tour's own effect,
   which runs after the render that brought the wave in; a digest gating on the flag alone would
   open in that gap, over a map about to fly away underneath it. */
export const selectTouredSpawns = (state) => state.ui.spawnTour.toured
export const selectSpawnTourRunning = (state) => state.ui.spawnTour.running

// Keyed on the four things an arrival is read from rather than on the whole game object, which
// is rebuilt by every payload: a chat line or a readiness flag used to rescan the round's events
// and hand `SpawnTour` a fresh empty array, re-rendering a component that renders nothing. The
// same frozen empty list comes back whenever nothing is queued, so the reference is stable too.
const NO_ARRIVALS = Object.freeze([])
export const selectSpawnArrivals = createSelector(
  [
    (state) => state.game.game?.id,
    (state) => state.game.game?.tick,
    (state) => state.game.game?.metadata?.events,
    (state) => state.game.game?.players,
    selectTouredSpawns,
  ],
  (id, tick, events, players, toured) => {
    const arrivals = spawnArrivals({ id, tick, metadata: { events }, players }, toured)
    return arrivals.length ? arrivals : NO_ARRIVALS
  }
)

export const selectSpawnTourBusy = createSelector(
  [selectSpawnArrivals, selectSpawnTourRunning],
  (arrivals, running) => running || arrivals.length > 0
)

export const selectFocusedCity = (state) => state.ui.focusedCity
export const selectCreatingMovement = (state) => state.ui.creatingMovement
export const selectCityModalSeq = (state) => state.ui.cityModalSeq
